import { Box, Typography, Card, CardContent, Grid, Button, Avatar, Chip } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchPlayer } from '../../services/api';
import { format } from 'date-fns';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const PlayerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: player, isLoading, error } = useQuery(
    ['player', id],
    () => fetchPlayer(id!),
    { enabled: !!id }
  );

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading player</div>;
  if (!player) return <div>Player not found</div>;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/players')}
        sx={{ mb: 2 }}
      >
        Back to Players
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                src={player.profile_image}
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
              >
                {player.first_name[0]}{player.last_name[0]}
              </Avatar>
              <Typography variant="h5" gutterBottom>
                {player.first_name} {player.last_name}
              </Typography>
              <Chip
                label={player.is_active ? 'Active' : 'Inactive'}
                color={player.is_active ? 'success' : 'default'}
                sx={{ mb: 2 }}
              />
              <Button variant="contained" fullWidth sx={{ mt: 2 }}>
                Edit Profile
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">{player.email}</Typography>
                </Grid>
                {player.phone && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">{player.phone}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Player Information
              </Typography>
              <Grid container spacing={2}>
                {player.ntrp_rating && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      NTRP Rating
                    </Typography>
                    <Typography variant="body1">{player.ntrp_rating}</Typography>
                  </Grid>
                )}
                {player.utr && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      UTR
                    </Typography>
                    <Typography variant="body1">{player.utr}</Typography>
                  </Grid>
                )}
                {player.gender && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Gender
                    </Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {player.gender}
                    </Typography>
                  </Grid>
                )}
                {player.date_of_birth && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(player.date_of_birth), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Joined
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(player.joined_at), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
                {player.last_played_at && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Last Played
                    </Typography>
                    <Typography variant="body1">
                      {format(new Date(player.last_played_at), 'MMM dd, yyyy')}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Match History
              </Typography>
              <Typography color="text.secondary">Match history coming soon</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlayerDetailPage;
